import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash-es";
import * as API from "services/api"
import { customNameForms, DEPARTMENT_KEY } from "utils/customNameHelpers"
import { errorToast } from "shared/toast"
import { nameForKey } from "./customNamesSlice"

const INITIAL_STATE = {
  loading: true,
  accessOverview: {
    members: [],
    nonMembersWithAccess: [],
  },
  data: {},
  submissionPermissions: {},
  tasks: [],
  attachments: [],
  showOnlyDeletedTasks: false,
  relatedFormSubmissions: [],
  questionUuidsIdentifyingTaskDepartments: [],
}

// These shadow the exported action creators of the same name. The slice must be
// created before action creators are available, but the thunks below reference
// them before the slice definition. Assigning here lets the thunks close over
// the variable; the real action creator is assigned after the slice is created.
let internalSetFormSubmissionData
let setSubmissionPermissions

export const updateAccessOverview = createAsyncThunk(
  "formSubmission/updateAccessOverview",
  async ({ formSubmissionSlug }) => {
    const response = await API.getFormSubmissionAccessOverview({ formSubmissionSlug })
    return response.data
  },
)

export const loadAssociatedFormSubmissionData = createAsyncThunk(
  "formSubmission/loadAssociatedFormSubmissionData",
  async ({ formSubmission, submissionPermissions }, { getState }) => {
    const { formSlug, slug: formSubmissionSlug } = formSubmission
    const { canViewUploadDownloadAttachments } = submissionPermissions

    const { pluralNameInSentence: departmentsName } = customNameForms(nameForKey(DEPARTMENT_KEY)(getState()))

    const associatedData = {
      attachments: [],
      questionUuidsIdentifyingTaskDepartments: [],
      relatedFormSubmissions: [],
      tasks: [],
    }

    if (canViewUploadDownloadAttachments) {
      const attachmentsResponse = await API.formSubmissionAttachments({ formSubmissionSlug })

      if (attachmentsResponse.ok) {
        associatedData.attachments = attachmentsResponse.data
      } else {
        throw new Error("Unable to load attachments.")
      }
    }

    const questionUuidsResponse = await API.formQuestionUuidsIdentifyingTaskDepartments({ formSlug })

    if (questionUuidsResponse.ok) {
      associatedData.questionUuidsIdentifyingTaskDepartments = questionUuidsResponse.data
    } else {
      throw new Error(`Unable to load questions identifying task ${departmentsName}.`)
    }

    const relatedFormSubmissionsResponse = await API.relatedFormSubmissions({ formSubmissionSlug })

    if (relatedFormSubmissionsResponse.ok) {
      associatedData.relatedFormSubmissions = relatedFormSubmissionsResponse.data
    } else {
      throw new Error("Unable to load related form submissions.")
    }

    const tasksResponse = await API.formSubmissionTasks({ formSubmissionSlug })

    if (tasksResponse.ok) {
      associatedData.tasks = tasksResponse.data
    } else {
      throw new Error("Unable to load tasks.")
    }

    return associatedData
  },
)

export const updateFormSubmission = createAsyncThunk(
  "formSubmission/updateFormSubmission",
  async ({ formSubmission, submissionPermissions }, thunkAPI) => {
    thunkAPI.dispatch(internalSetFormSubmissionData(formSubmission))
    thunkAPI.dispatch(setSubmissionPermissions(submissionPermissions))
    thunkAPI.dispatch(loadAssociatedFormSubmissionData({ formSubmission, submissionPermissions }))
  },
)

export const formSubmissionSlice = createSlice({
  name: "formSubmission",
  initialState: INITIAL_STATE,
  reducers: {
    resetData: (state) => {
      const resetState = cloneDeep(INITIAL_STATE)
      delete resetState.loading

      Object.assign(state, resetState)
    },
    setFormSubmissionData: (state, action) => {
      state.data = action.payload
    },
    updateFormSubmissionAnswers: (state, action) => {
      state.data.answers = action.payload
    },
    setSubmissionPermissions: (state, action) => {
      state.submissionPermissions = action.payload
    },
    setRelatedFormSubmissions: (state, action) => {
      state.relatedFormSubmissions = action.payload
    },
    setTasks: (state, { payload }) => {
      payload.forEach((task) => { task.expanded = false })
      state.tasks = payload
    },
    toggleTaskExpanded: (state, { payload: taskId }) => {
      const task = state.tasks.find(({ id }) => id === taskId)
      task.expanded = !task.expanded
    },
    setAttachments: (state, action) => {
      state.attachments = action.payload
    },
    addAttachment: (state, action) => {
      state.attachments.push(action.payload)
    },
    addTask: (state, { payload }) => {
      payload.expanded = false
      state.tasks.push(payload)
    },
    updateTask: (state, action) => {
      const { payload } = action
      const { id } = payload

      const taskIndex = state.tasks.findIndex((task) => task.id === id)
      state.tasks[taskIndex] = payload
    },
    updateTaskAnswer: (state, { payload }) => {
      const { answer, id, questionUuid } = payload

      const taskIndex = state.tasks.findIndex((task) => task.id === id)
      state.tasks[taskIndex].answers[questionUuid] = answer
    },
    deleteTaskAnswer: (state, { payload }) => {
      const { id, questionUuid } = payload

      const taskIndex = state.tasks.findIndex((task) => task.id === id)
      delete state.tasks[taskIndex].answers[questionUuid]
    },
    removeTask: (state, { payload: taskId }) => {
      const task = state.tasks.find(({ id }) => id === taskId)
      task.isDeleted = true
    },
    recoverTask: (state, { payload: taskId }) => {
      const task = state.tasks.find(({ id }) => id === taskId)
      task.isDeleted = false
    },
    setShowDeletedTasks: (state, action) => {
      state.showOnlyDeletedTasks = action.payload
    },
    closeSubmission: (state, action) => {
      state.data.statusName = action.payload
      state.data.isClosed = true
    },
    reopenSubmission: (state, action) => {
      state.data.statusName = action.payload
      state.data.isClosed = false
    },
    setSubmissionStatus: (state, action) => {
      state.data.statusName = action.payload
    },
    setQuestionUuidsIdentifyingTaskDepartments: (state, action) => {
      state.questionUuidsIdentifyingTaskDepartments = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // Update access overview
      .addCase(updateAccessOverview.fulfilled, (state, action) => {
        state.accessOverview = action.payload
      })
      // Load associated form submission data
      .addCase(loadAssociatedFormSubmissionData.pending, (state) => {
        state.loading = true
      })
      .addCase(loadAssociatedFormSubmissionData.fulfilled, (state, action) => {
        const {
          attachments, questionUuidsIdentifyingTaskDepartments, relatedFormSubmissions, tasks,
        } = action.payload

        tasks.forEach((task) => { task.expanded = false })

        state.attachments = attachments
        state.questionUuidsIdentifyingTaskDepartments = questionUuidsIdentifyingTaskDepartments
        state.relatedFormSubmissions = relatedFormSubmissions
        state.tasks = tasks

        state.loading = false
      })
      .addCase(loadAssociatedFormSubmissionData.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
  },
})

// Variables declared before async thunks
internalSetFormSubmissionData = formSubmissionSlice.actions.setFormSubmissionData
setSubmissionPermissions = formSubmissionSlice.actions.setSubmissionPermissions

export const {
  resetData,
  setAccessOverview,
  setForm,
  setFormSubmissionData,
  updateFormSubmissionAnswers,
  setRelatedFormSubmissions,
  setTasks,
  toggleTaskExpanded,
  setAttachments,
  addAttachment,
  addTask,
  updateTask,
  updateTaskAnswer,
  deleteTaskAnswer,
  removeTask,
  recoverTask,
  setShowDeletedTasks,
  closeSubmission,
  reopenSubmission,
  setSubmissionStatus,
  setQuestionUuidsIdentifyingTaskDepartments,
} = formSubmissionSlice.actions

export const accessOverview = (state) => state.formSubmission.accessOverview

export const formSubmissionMembers = (state) => state.formSubmission.accessOverview.members

export const formSubmissionNonMembersWithAccess = (state) => state.formSubmission.accessOverview.nonMembersWithAccess

export const submitterFullName = (state) => state.formSubmission.data.submitterFullName

export const tracked = (state) => state.formSubmission.data.tracked

export const form = (state) => state.formSubmission.form

export const formSubmissionBatchSentAt = (state) => state.formSubmission.data.batchSentAt

export const formSubmissionClosed = (state) => state.formSubmission.data.isClosed

export const formSubmissionCreatedAt = (state) => state.formSubmission.data.createdAt

export const formSubmissionCreatedOnInOrganizationTimeZone = (state) => (
  state.formSubmission.data.createdOnInOrganizationTimeZone
)

export const formSubmissionAnswers = (state) => state.formSubmission.data.answers

export const formSubmissionData = (state) => state.formSubmission.data

export const formSubmissionFlagCount = (state) => state.formSubmission.data.flagCount

export const formSubmissionFlaggedQuestionUuids = (state) => state.formSubmission.data.flaggedQuestionUuids

export const formSubmissionIsClosed = (state) => state.formSubmission.data.isClosed

export const formSubmissionId = (state) => state.formSubmission.data.id

export const formSubmissionSlug = (state) => state.formSubmission.data.slug

export const formSubmissionStatusName = (state) => state.formSubmission.data.statusName

export const submissionPermissions = (state) => state.formSubmission.submissionPermissions

export const relatedFormSubmissions = (state) => state.formSubmission.relatedFormSubmissions

export const allTasks = (state) => state.formSubmission.tasks

export const accessibleTasks = (state) => (
  state.formSubmission.tasks.filter((task) => task.userTaskPermissions.canSeeDetails)
)

export const getUndiscardedTasks = (state) => state.formSubmission.tasks.filter((task) => !task.isDeleted)

export const getTasks = (state) => {
  const { tasks, showOnlyDeletedTasks } = state.formSubmission

  let filteredTasks = tasks

  if (showOnlyDeletedTasks) {
    filteredTasks = filteredTasks.filter((task) => task.isDeleted)
  } else {
    filteredTasks = getUndiscardedTasks(state)
  }

  return filteredTasks
}

export const getTaskUserIds = (taskId) => (state) => {
  const task = state.formSubmission.tasks.find((t) => t.id === taskId)

  if (!task) return []

  return task.assigneeIds.concat(task.memberIds)
}

export const getListedTaskIds = (state) => getTasks(state).map((task) => task.id)

export const allFormSubmissionTasksClosed = (state) => getTasks(state).every((task) => !task.isOpen)

export const loading = (state) => state.formSubmission.loading

export const showingDeletedTasks = (state) => state.formSubmission.showOnlyDeletedTasks

export const attachments = (state) => state.formSubmission.attachments

export const attachmentCount = (state) => attachments(state).length

export const questionUuidsIdentifyingTaskDepartments = (state) => state.formSubmission.questionUuidsIdentifyingTaskDepartments

export default formSubmissionSlice.reducer
